import type { FundraisingResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import {
  type FundraisingDraft,
  toCreateRequest,
} from '../../_shared/domain/fundraising-draft.model';
import { validateDraft } from '../../_shared/domain/fundraising-draft.validation';
import { toFundraisingFormError } from '../../_shared/domain/fundraising-form-error';
import type { EditFundraisingGateway } from '../infrastructure/edit-fundraising.gateway';
import {
  deleteRequested,
  loadRequested,
  revertRequested,
  submitRequested,
} from './edit-fundraising.actions';
import { selectDraft, selectFundraising } from './edit-fundraising.selectors';
import { editFundraisingActions } from './edit-fundraising.slice';

/**
 * WORKER: hydrate the round being edited. All I/O goes through the injected
 * gateway (read from the saga `context`).
 */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: EditFundraisingGateway = yield getContext('editFundraisingGateway');
  yield put(editFundraisingActions.loadStarted());
  try {
    const fundraising = (yield call(
      gateway.loadFundraising,
      action.payload,
    )) as FundraisingResponse;
    yield put(editFundraisingActions.loadSucceeded(fundraising));
  } catch (e) {
    yield put(
      editFundraisingActions.loadFailed(toFundraisingFormError(e, 'Could not load the round.')),
    );
  }
}

/**
 * WORKER: persist the edited draft. Validates first (pure domain); on failure
 * dispatches a `validation` error and stops without touching the gateway. The
 * PATCH body is the full mapped draft, exactly like the flat page sent.
 */
function* saveWorker() {
  const fundraising = (yield select(selectFundraising)) as FundraisingResponse | null;
  if (!fundraising) return;
  const draft = (yield select(selectDraft)) as FundraisingDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(editFundraisingActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: EditFundraisingGateway = yield getContext('editFundraisingGateway');
  yield put(editFundraisingActions.saveStarted());
  try {
    yield call(gateway.updateFundraising, fundraising.id, toCreateRequest(draft));
    yield put(editFundraisingActions.saveSucceeded());
  } catch (e) {
    yield put(
      editFundraisingActions.saveFailed(
        toFundraisingFormError(e, 'Could not save the round. Try again.'),
      ),
    );
  }
}

/** WATCHERS. */
function* deleteWorker(action: ReturnType<typeof deleteRequested>) {
  const gateway: EditFundraisingGateway = yield getContext('editFundraisingGateway');
  yield put(editFundraisingActions.deleteStarted());
  try {
    yield call(gateway.deleteFundraising, action.payload);
    yield put(editFundraisingActions.deleteSucceeded());
  } catch (e) {
    yield put(
      editFundraisingActions.deleteFailed(
        toFundraisingFormError(e, 'Could not delete the round. Try again.'),
      ),
    );
  }
}

function* revertWorker(action: ReturnType<typeof revertRequested>) {
  const gateway: EditFundraisingGateway = yield getContext('editFundraisingGateway');
  yield put(editFundraisingActions.revertStarted());
  try {
    yield call(gateway.updateFundraising, action.payload.id, action.payload.request);
    yield put(editFundraisingActions.revertSucceeded());
  } catch {
    yield put(editFundraisingActions.revertFailed());
  }
}

export function* editFundraisingSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(submitRequested.type, saveWorker),
    takeLatest(deleteRequested.type, deleteWorker),
    takeLatest(revertRequested.type, revertWorker),
  ]);
}
